import { useEffect, useMemo, useState, useRef, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, TextField, Button, Stack, MenuItem, Divider,
  Grid, FormControlLabel, Switch, Alert, IconButton, InputAdornment,
  LinearProgress, Chip, Autocomplete,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import GeoMasterSelect from '../masters/GeoMasterSelect';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import { usePlatformServices } from '../../hooks/useServiceCatalog';
import { filterTenantServiceModules } from '../../constants/tenantModules';
import { defaultsForCountrySelection, currencyOptionsForCountry, timezoneOptionsForCountry } from '../../constants/countryDefaults';
import {
  slugifyCompany, checkTenantDomainAvailability, getTenantAdmin, type TenantFormPayload, type TenantRow,
} from '../../services/tenantService';
import { phoneLengthForDialCode, listCountryDialCodes } from '../../services/countryDialCodeService';
import { composeE164, splitPhoneByDialCodes, localPhoneDigits } from '../../utils/phoneDialCode';
import { validatePersonName, validateEmail, validateLocalPhoneForCountry } from '../../utils/personFieldValidation';
import {
  sanitizeTenantCompanyName,
  sanitizeTenantSlug,
  sanitizeTenantDomain,
  sanitizeTenantSubdomain,
  validateTenantCompanyName,
  validateTenantSlug,
  validateTenantDomainConfig,
  TENANT_COMPANY_NAME_MAX,
  TENANT_SLUG_MAX,
} from '../../utils/tenantFormValidation';
import {
  PLATFORM_LANGUAGE_OPTIONS,
  resolveLanguageOptions,
  type PlatformLanguageOption,
} from '../../constants/platformLanguages';

function createEmptyTenantForm(enabledModules: string[] = ['transportation']): TenantFormPayload {
  return {
    companyName: '',
    slug: '',
    domain: '',
    subdomain: '',
    country: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    defaultLanguage: 'en',
    languages: ['en'],
    subscriptionPlan: '',
    subscriptionExpiry: '',
    primaryColor: '#173A5E',
    secondaryColor: '#5C6B7A',
    logo: '',
    favicon: '',
    fontFamily: 'Inter, sans-serif',
    enabledModules: [...enabledModules],
    adminUser: { email: '', password: '', firstName: '', lastName: '', phone: '', phoneCountryCode: '' },
  };
}

/** Section titles for the single-page form (all shown at once). */
const SECTION_META = [
  { title: 'Company', subtitle: 'Name and URL slug' },
  { title: 'Locale', subtitle: 'Country, currency, timezone, and domain' },
  { title: 'Branding', subtitle: 'Colors, logo, and typography' },
  { title: 'Modules', subtitle: 'Enabled platform modules' },
  { title: 'Admin', subtitle: 'Client admin login details' },
] as const;

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  tenant?: TenantRow | null;
  loading?: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (payload: TenantFormPayload) => void;
}

function validateTenantForm(
  form: TenantFormPayload,
  mode: 'create' | 'edit',
  extras?: { confirmPassword?: string },
): Record<string, string> {
  const errors: Record<string, string> = {};
  const companyError = validateTenantCompanyName(form.companyName);
  if (companyError) errors.companyName = companyError;
  const slugError = validateTenantSlug(form.slug);
  if (slugError) errors.slug = slugError;

  if (!form.languages.length) errors.languages = 'Select at least one language';
  if (form.defaultLanguage && !form.languages.includes(form.defaultLanguage)) {
    errors.defaultLanguage = 'Default language must be one of the selected languages';
  }
  const domainConfigErrors = validateTenantDomainConfig(form.domain || '', form.subdomain || '');
  if (domainConfigErrors.domain) errors.domain = domainConfigErrors.domain;
  if (domainConfigErrors.subdomain) errors.subdomain = domainConfigErrors.subdomain;

  const email = form.adminUser?.email?.trim() || '';
  const password = form.adminUser?.password || '';
  if (email) {
    const emailErr = validateEmail(email, 'Admin email', { required: true });
    if (emailErr) errors.adminEmail = emailErr;
    if (!form.adminUser?.firstName?.trim()) {
      errors.adminFirstName = 'Admin first name is required when email is set';
    } else {
      const nameErr = validatePersonName(form.adminUser.firstName, 'Admin first name', { required: true });
      if (nameErr) errors.adminFirstName = nameErr;
    }
    const lastErr = validatePersonName(form.adminUser?.lastName || '', 'Admin last name');
    if (lastErr) errors.adminLastName = lastErr;

    if (mode === 'create') {
      if (!password || password.length < 6) {
        errors.adminPassword = 'Password must be at least 6 characters';
      }
      const confirm = extras?.confirmPassword ?? '';
      if (!confirm) errors.adminConfirmPassword = 'Confirm password is required';
      else if (password !== confirm) errors.adminConfirmPassword = 'Passwords do not match';
    } else if (password) {
      if (password.length < 6) errors.adminPassword = 'Password must be at least 6 characters';
      const confirm = extras?.confirmPassword ?? '';
      if (!confirm) errors.adminConfirmPassword = 'Confirm password is required';
      else if (password !== confirm) errors.adminConfirmPassword = 'Passwords do not match';
    }
  }
  const adminPhone = form.adminUser?.phone || '';
  const adminDial = form.adminUser?.phoneCountryCode || '';
  if (adminPhone.trim()) {
    if (!adminDial) errors.adminPhone = 'Select a country code';
    else {
      const phoneErr = validateLocalPhoneForCountry(
        adminPhone,
        phoneLengthForDialCode(adminDial),
        'Admin phone',
      );
      if (phoneErr) errors.adminPhone = phoneErr;
    }
  }
  return errors;
}

export default function TenantFormDrawer({ open, mode, tenant, loading, error, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<TenantFormPayload>(() => createEmptyTenantForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [autoSlug, setAutoSlug] = useState(mode === 'create');
  const bodyRef = useRef<HTMLDivElement>(null);
  const adminHydratedRef = useRef(false);
  const { data: platformServices = [] } = usePlatformServices();
  const serviceModules = useMemo(() => filterTenantServiceModules(platformServices), [platformServices]);
  const currencyOptions = useMemo(
    () => currencyOptionsForCountry(form.country, form.currency),
    [form.country, form.currency],
  );
  const timezoneOptions = useMemo(
    () => timezoneOptionsForCountry(form.country, form.timezone),
    [form.country, form.timezone],
  );

  const patch = (p: Partial<TenantFormPayload>) => setForm((f) => ({ ...f, ...p }));
  const formExtras = useMemo(() => ({ confirmPassword }), [confirmPassword]);

  const { data: existingAdmin, isLoading: adminLoading, isFetched: adminFetched } = useQuery({
    queryKey: ['tenant-admin', tenant?._id],
    queryFn: () => getTenantAdmin(tenant!._id),
    enabled: open && mode === 'edit' && !!tenant?._id,
    staleTime: 30_000,
  });

  const { data: dialOptions = [] } = useQuery({
    queryKey: ['country-dial-codes'],
    queryFn: listCountryDialCodes,
    staleTime: 5 * 60_000,
    enabled: open,
  });

  const selectedLanguages = useMemo(
    () => resolveLanguageOptions(form.languages),
    [form.languages],
  );

  const onLanguagesChange = (options: PlatformLanguageOption[]) => {
    const codes = options.map((o) => o.code);
    const defaultLanguage = codes.includes(form.defaultLanguage) ? form.defaultLanguage : (codes[0] || 'en');
    patch({ languages: codes.length ? codes : ['en'], defaultLanguage });
  };

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAutoSlug(mode === 'create');
    adminHydratedRef.current = false;
    if (tenant && mode === 'edit') {
      const serviceCodes = new Set(serviceModules.map((m) => m.code));
      const enabledFromTenant = (tenant.modules || [])
        .filter((m) => m.enabled)
        .map((m) => m.key);
      const enabledModules = serviceCodes.size
        ? enabledFromTenant.filter((k) => serviceCodes.has(k))
        : enabledFromTenant;
      setForm({
        companyName: tenant.companyName,
        slug: tenant.slug,
        domain: tenant.domain || '',
        subdomain: tenant.subdomain || '',
        country: tenant.country || 'IN',
        currency: tenant.currency || 'INR',
        timezone: tenant.timezone || 'Asia/Kolkata',
        defaultLanguage: tenant.defaultLanguage || 'en',
        languages: tenant.languages?.length ? tenant.languages : ['en'],
        subscriptionPlan: '',
        subscriptionExpiry: '',
        primaryColor: tenant.branding?.primaryColor || '#173A5E',
        secondaryColor: tenant.branding?.secondaryColor || '#5C6B7A',
        logo: tenant.branding?.logo || '',
        favicon: tenant.branding?.favicon || '',
        fontFamily: tenant.branding?.fontFamily || 'Inter, sans-serif',
        enabledModules,
        adminUser: { email: '', password: '', firstName: '', lastName: '', phone: '', phoneCountryCode: '' },
      });
    } else {
      const known = serviceModules.map((m) => m.code);
      const preferred = ['transportation', 'porter', 'on_demand'].filter((c) => known.includes(c));
      setForm(createEmptyTenantForm(preferred.length ? preferred : known.slice(0, 2)));
    }
  }, [open, mode, tenant]);

  // Align create defaults with the live catalog once services load (without wiping typed fields).
  useEffect(() => {
    if (!open || mode !== 'create' || !serviceModules.length) return;
    setForm((f) => {
      const known = new Set(serviceModules.map((m) => m.code));
      const kept = f.enabledModules.filter((k) => known.has(k));
      if (kept.length === f.enabledModules.length) return f;
      if (kept.length) return { ...f, enabledModules: kept };
      if (f.companyName.trim() || f.slug.trim()) return f;
      const preferred = ['transportation', 'porter', 'on_demand'].filter((c) => known.has(c));
      return {
        ...f,
        enabledModules: preferred.length ? preferred : [serviceModules[0].code],
      };
    });
  }, [open, mode, serviceModules]);

  // Populate Admin section from existing client_admin when editing.
  useEffect(() => {
    if (!open || mode !== 'edit' || adminHydratedRef.current || !adminFetched) return;
    if (!existingAdmin) {
      adminHydratedRef.current = true;
      return;
    }
    const rawPhone = existingAdmin.phone || '';
    if (rawPhone.startsWith('+') && !dialOptions.length) return;
    const dialCodes = dialOptions.map((o) => o.dialCode);
    const split = splitPhoneByDialCodes(rawPhone, dialCodes);
    adminHydratedRef.current = true;
    setForm((f) => ({
      ...f,
      adminUser: {
        email: existingAdmin.email || '',
        password: '',
        firstName: existingAdmin.firstName || '',
        lastName: existingAdmin.lastName || '',
        phone: split.localNumber || localPhoneDigits(rawPhone),
        phoneCountryCode: split.dialCode || '',
      },
    }));
    setConfirmPassword('');
  }, [open, mode, existingAdmin, dialOptions, adminFetched]);

  useEffect(() => {
    if (!error) return;
    const lower = error.toLowerCase();
    if (lower.includes('custom domain') || (lower.includes('domain') && lower.includes('already'))) {
      setErrors((prev) => ({ ...prev, domain: error }));
    } else if (lower.includes('subdomain') && lower.includes('already')) {
      setErrors((prev) => ({ ...prev, subdomain: error }));
    }
  }, [error]);

  useEffect(() => {
    if (autoSlug && form.companyName) patch({ slug: slugifyCompany(form.companyName) });
  }, [form.companyName, autoSlug]);

  const checkDomainUniqueness = async (): Promise<Record<string, string>> => {
    const domain = sanitizeTenantDomain(form.domain || '');
    const subdomain = sanitizeTenantSubdomain(form.subdomain || '');
    if (!domain && !subdomain) return {};
    try {
      const result = await checkTenantDomainAvailability({
        domain: domain || undefined,
        subdomain: subdomain || undefined,
        excludeId: mode === 'edit' ? tenant?._id : undefined,
      });
      const next: Record<string, string> = {};
      if (!result.domainAvailable) next.domain = result.domainError || 'Custom domain is already assigned to another tenant';
      if (!result.subdomainAvailable) next.subdomain = result.subdomainError || 'Subdomain is already assigned to another tenant';
      return next;
    } catch {
      return {};
    }
  };

  const scrollToFirstInvalid = () => {
    requestAnimationFrame(() => {
      const invalid = bodyRef.current?.querySelector('[aria-invalid="true"]') as HTMLElement | null;
      invalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleSave = async () => {
    const formErrors = validateTenantForm(form, mode, formExtras);
    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      scrollToFirstInvalid();
      return;
    }
    const uniqueErrors = await checkDomainUniqueness();
    if (Object.keys(uniqueErrors).length) {
      setErrors(uniqueErrors);
      scrollToFirstInvalid();
      return;
    }
    onSubmit({
      ...form,
      domain: sanitizeTenantDomain(form.domain || ''),
      subdomain: sanitizeTenantSubdomain(form.subdomain || ''),
      subscriptionPlan: '',
      subscriptionExpiry: '',
      adminUser: form.adminUser?.email
        ? {
            email: form.adminUser.email,
            password: form.adminUser.password || '',
            firstName: form.adminUser.firstName,
            lastName: form.adminUser.lastName,
            phone: form.adminUser.phone
              ? composeE164(form.adminUser.phoneCountryCode || '', form.adminUser.phone)
              : undefined,
          }
        : undefined,
    });
  };

  const section = (title: string, subtitle: string, children: ReactNode) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25 }}>
        {subtitle}
      </Typography>
      {children}
      <Divider sx={{ mt: 2.5 }} />
    </Box>
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560, md: 640 } } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {mode === 'create' ? 'Create tenant' : `Edit · ${tenant?.companyName || 'Tenant'}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Company, locale, branding, modules, and admin in one form
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><Close /></IconButton>
        </Stack>

        {mode === 'edit' && adminLoading && <LinearProgress sx={{ height: 3 }} />}

        <Box ref={bodyRef} sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {section(SECTION_META[0].title, SECTION_META[0].subtitle, (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" required label="Company name" value={form.companyName}
                  error={!!errors.companyName}
                  helperText={errors.companyName || `Letters, numbers, spaces, & . - ' only · max ${TENANT_COMPANY_NAME_MAX} characters`}
                  inputProps={{ maxLength: TENANT_COMPANY_NAME_MAX }}
                  onChange={(e) => patch({ companyName: sanitizeTenantCompanyName(e.target.value) })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" required label="Slug" value={form.slug}
                  disabled={mode === 'edit' && !autoSlug}
                  error={!!errors.slug}
                  helperText={errors.slug || (autoSlug ? 'Auto from company name' : `Lowercase, numbers, hyphens only · max ${TENANT_SLUG_MAX}`)}
                  inputProps={{ maxLength: TENANT_SLUG_MAX }}
                  onChange={(e) => patch({ slug: sanitizeTenantSlug(e.target.value) })}
                  InputProps={{
                    endAdornment: mode === 'create' ? (
                      <InputAdornment position="end">
                        <FormControlLabel control={<Switch size="small" checked={autoSlug} onChange={(e) => setAutoSlug(e.target.checked)} />} label="Auto" />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Grid>
            </Grid>
          ))}

          {section(SECTION_META[1].title, SECTION_META[1].subtitle, (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <GeoMasterSelect masterType="countries" label="Country" value={form.country} required
                  onChange={(code, opt) => {
                    const d = defaultsForCountrySelection(code, opt);
                    const admin = form.adminUser || {
                      email: '', password: '', firstName: '', lastName: '', phone: '', phoneCountryCode: '',
                    };
                    // When phone is still empty, reset dial code so it follows the new tenant country.
                    patch({
                      country: code,
                      timezone: d.timezone,
                      currency: d.currency,
                      adminUser: admin.phone?.trim()
                        ? admin
                        : { ...admin, phoneCountryCode: '' },
                    });
                  }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" select label="Currency" value={form.currency}
                  helperText="Options depend on the selected country"
                  onChange={(e) => patch({ currency: e.target.value })}>
                  {currencyOptions.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" select label="Timezone" value={form.timezone}
                  helperText="Options depend on the selected country"
                  onChange={(e) => patch({ timezone: e.target.value })}>
                  {timezoneOptions.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Default language"
                  value={selectedLanguages.some((l) => l.code === form.defaultLanguage) ? form.defaultLanguage : (selectedLanguages[0]?.code || '')}
                  error={!!errors.defaultLanguage}
                  helperText={errors.defaultLanguage}
                  disabled={!selectedLanguages.length}
                  onChange={(e) => patch({ defaultLanguage: e.target.value })}
                >
                  {!selectedLanguages.length && <MenuItem value="">Select languages first</MenuItem>}
                  {selectedLanguages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name} · {lang.nativeName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  options={PLATFORM_LANGUAGE_OPTIONS}
                  value={selectedLanguages}
                  getOptionLabel={(opt) => `${opt.name} (${opt.nativeName})`}
                  isOptionEqualToValue={(a, b) => a.code === b.code}
                  onChange={(_, value) => onLanguagesChange(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip {...getTagProps({ index })} key={option.code} label={option.code.toUpperCase()} size="small" />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Supported languages"
                      placeholder="Select languages…"
                      error={!!errors.languages}
                      helperText={errors.languages || 'Multi-select — tenant users can switch between these'}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Custom domain"
                  placeholder="e.g. app.client.com"
                  InputLabelProps={{ shrink: true }}
                  value={form.domain || ''}
                  error={!!errors.domain}
                  helperText={
                    errors.domain
                    || 'Optional · full hostname: letters, numbers, hyphens, dots (e.g. app.client.com)'
                  }
                  onChange={(e) => patch({ domain: e.target.value })}
                  onBlur={async () => {
                    const config = validateTenantDomainConfig(form.domain || '', form.subdomain || '');
                    if (config.domain || config.subdomain) {
                      setErrors((prev) => ({
                        ...prev,
                        ...(config.domain ? { domain: config.domain } : {}),
                        ...(config.subdomain ? { subdomain: config.subdomain } : {}),
                      }));
                      if (config.domain) return;
                    } else {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.domain;
                        if (!form.subdomain?.trim()) delete next.subdomain;
                        return next;
                      });
                    }
                    if (!form.domain?.trim()) return;
                    const unique = await checkDomainUniqueness();
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (unique.domain) next.domain = unique.domain;
                      else if (!config.domain) delete next.domain;
                      return next;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Subdomain"
                  placeholder="e.g. client"
                  InputLabelProps={{ shrink: true }}
                  value={form.subdomain || ''}
                  error={!!errors.subdomain}
                  helperText={
                    errors.subdomain
                    || 'Optional · one label: lowercase letters, numbers, hyphens (e.g. client)'
                  }
                  onChange={(e) => patch({ subdomain: e.target.value })}
                  onBlur={async () => {
                    const config = validateTenantDomainConfig(form.domain || '', form.subdomain || '');
                    if (config.domain || config.subdomain) {
                      setErrors((prev) => ({
                        ...prev,
                        ...(config.domain ? { domain: config.domain } : {}),
                        ...(config.subdomain ? { subdomain: config.subdomain } : {}),
                      }));
                      if (config.subdomain) return;
                    } else {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.subdomain;
                        if (!form.domain?.trim()) delete next.domain;
                        return next;
                      });
                    }
                    if (!form.subdomain?.trim()) return;
                    const unique = await checkDomainUniqueness();
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (unique.subdomain) next.subdomain = unique.subdomain;
                      else if (!config.subdomain) delete next.subdomain;
                      return next;
                    });
                  }}
                />
              </Grid>
            </Grid>
          ))}

          {section(SECTION_META[2].title, SECTION_META[2].subtitle, (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Primary color" type="color" value={form.primaryColor}
                  onChange={(e) => patch({ primaryColor: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Secondary color" type="color" value={form.secondaryColor}
                  onChange={(e) => patch({ secondaryColor: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <FormFileUpload label="Logo" value={form.logo || ''} onChange={(url) => patch({ logo: url })}
                  folder="tenants/branding/logo" accept="image/png,image/jpeg,image/webp,image/svg+xml" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormFileUpload label="Favicon" value={form.favicon || ''} onChange={(url) => patch({ favicon: url })}
                  folder="tenants/branding/favicon" accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/jpeg,.ico" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Font family" value={form.fontFamily}
                  onChange={(e) => patch({ fontFamily: e.target.value })} />
              </Grid>
            </Grid>
          ))}

          {section(SECTION_META[3].title, SECTION_META[3].subtitle, (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Enable services for this tenant. Selections are saved with the tenant
                ({form.enabledModules.filter((k) => serviceModules.some((s) => s.code === k)).length} selected).
              </Typography>
              <Grid container spacing={1}>
                {serviceModules.map((mod) => (
                  <Grid item xs={12} sm={6} key={mod.code}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.enabledModules.includes(mod.code)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setForm((f) => ({
                              ...f,
                              enabledModules: checked
                                ? (f.enabledModules.includes(mod.code)
                                  ? f.enabledModules
                                  : [...f.enabledModules, mod.code])
                                : f.enabledModules.filter((k) => k !== mod.code),
                            }));
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{mod.name}</Typography>
                          {mod.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {mod.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Grid>
                ))}
                {!serviceModules.length && (
                  <Grid item xs={12}>
                    <Alert severity="info" variant="outlined">No service modules available.</Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}

          {section(SECTION_META[4].title, SECTION_META[4].subtitle, (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info" variant="outlined">
                  {mode === 'create'
                    ? `Optional — create the first client admin. On create, a welcome message with login URL, password, and enabled features is sent to the admin email${form.adminUser?.phone?.trim() ? ' and phone' : ''}.`
                    : adminLoading
                      ? 'Loading existing client admin…'
                      : existingAdmin
                        ? 'Update the client admin profile below. Leave password blank to keep the current password.'
                        : 'No client admin yet — enter details below to create one. Password is required for a new admin.'}
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <PersonNameField fullWidth size="small" label="Admin first name" value={form.adminUser?.firstName || ''}
                  error={!!errors.adminFirstName} helperText={errors.adminFirstName}
                  onChange={(e) => patch({ adminUser: { ...form.adminUser!, firstName: e.target.value } })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PersonNameField fullWidth size="small" label="Admin last name" value={form.adminUser?.lastName || ''}
                  error={!!errors.adminLastName} helperText={errors.adminLastName}
                  onChange={(e) => patch({ adminUser: { ...form.adminUser!, lastName: e.target.value } })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Admin email" type="email"
                  placeholder="admin@company.com"
                  value={form.adminUser?.email || ''}
                  error={!!errors.adminEmail}
                  helperText={errors.adminEmail || (mode === 'create' ? 'Welcome email is sent here after create' : 'Client admin login email')}
                  onChange={(e) => patch({ adminUser: { ...form.adminUser!, email: e.target.value } })}
                  onBlur={() => {
                    if (!form.adminUser?.email?.trim()) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.adminEmail;
                        return next;
                      });
                      return;
                    }
                    const emailErr = validateEmail(form.adminUser.email, 'Admin email', { required: true });
                    setErrors((prev) => {
                      const next = { ...prev };
                      if (emailErr) next.adminEmail = emailErr;
                      else delete next.adminEmail;
                      return next;
                    });
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <PhoneWithDialCodeField
                  label="Admin phone"
                  phone={form.adminUser?.phone || ''}
                  countryCode={form.adminUser?.phoneCountryCode || ''}
                  preferredCountry={form.country || 'IN'}
                  error={!!errors.adminPhone}
                  helperText={errors.adminPhone || 'Optional — country code follows tenant country'}
                  onChange={({ phone, countryCode }) => patch({
                    adminUser: {
                      email: form.adminUser?.email || '',
                      password: form.adminUser?.password || '',
                      firstName: form.adminUser?.firstName || '',
                      lastName: form.adminUser?.lastName || '',
                      phone,
                      phoneCountryCode: countryCode,
                    },
                  })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Admin password" type={showPassword ? 'text' : 'password'}
                  value={form.adminUser?.password || ''}
                  error={!!errors.adminPassword}
                  helperText={
                    errors.adminPassword
                    || (mode === 'edit'
                      ? 'Leave blank to keep current password'
                      : 'Min 6 characters if email is set')
                  }
                  onChange={(e) => {
                    patch({ adminUser: { ...form.adminUser!, password: e.target.value } });
                    if (errors.adminConfirmPassword && confirmPassword && e.target.value === confirmPassword) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.adminConfirmPassword;
                        return next;
                      });
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Confirm password" type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  error={!!errors.adminConfirmPassword}
                  helperText={
                    errors.adminConfirmPassword
                    || (mode === 'edit' ? 'Required only when setting a new password' : 'Must match admin password')
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setConfirmPassword(value);
                    setErrors((prev) => {
                      const next = { ...prev };
                      const pwd = form.adminUser?.password || '';
                      if (!pwd && mode === 'edit') {
                        delete next.adminConfirmPassword;
                        return next;
                      }
                      if (!value && (mode === 'create' || pwd)) {
                        next.adminConfirmPassword = 'Confirm password is required';
                      } else if (value && value !== pwd) {
                        next.adminConfirmPassword = 'Passwords do not match';
                      } else {
                        delete next.adminConfirmPassword;
                      }
                      return next;
                    });
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </Box>

        <Divider />
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="success" disabled={loading} onClick={handleSave}>
            {loading ? 'Saving…' : mode === 'create' ? 'Create tenant' : 'Save changes'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
